/**
 * Validation Module
 * 
 * Comprehensive validation functions for invoice data.
 * All validators return consistent results for easy integration.
 * 
 * Includes user-friendly error message generators for inline validation.
 */

import { toNumber, hasValidLineItems } from './calculations';

// ============================================================================
// USER-FRIENDLY ERROR MESSAGES
// ============================================================================

/**
 * Error message constants for consistent, human-friendly feedback
 */
export const ERROR_MESSAGES = {
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid phone number',
  required: (field) => `${field} is required`,
  positiveNumber: 'Please enter a positive number',
  quantity: 'Quantity must be greater than 0',
  rate: 'Rate must be 0 or greater',
  discount: 'Discount cannot exceed the subtotal',
  discountNegative: 'Discount cannot be negative',
  taxRate: 'Tax rate must be between 0% and 100%',
  dueDateBefore: 'Due date cannot be before invoice date',
  lineItemRequired: 'At least one line item is required',
  businessName: 'Business name is required',
  clientName: 'Client name is required',
  invoiceNumber: 'Invoice number is required',
};

// ============================================================================
// VALIDATION RESULT HELPERS
// ============================================================================

/**
 * Create a validation result object
 * @param {boolean} isValid - Whether the value is valid
 * @param {string|null} message - Error message if invalid
 * @returns {Object} Validation result
 */
export function createValidationResult(isValid, message = null) {
  return { isValid, message: isValid ? null : message };
}

// ============================================================================
// BASIC VALIDATORS WITH MESSAGES
// ============================================================================

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.trim());
}

/**
 * Validate email with error message
 * @param {string} email - Email to validate
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Object} { isValid, message }
 */
export function validateEmailWithMessage(email, isRequired = false) {
  if (!email || !email.trim()) {
    return isRequired 
      ? createValidationResult(false, ERROR_MESSAGES.required('Email'))
      : createValidationResult(true);
  }
  return createValidationResult(validateEmail(email), ERROR_MESSAGES.email);
}

/**
 * Validate required field (non-empty string)
 * @param {string} value - Value to validate
 * @returns {boolean} Whether value is non-empty
 */
export function validateRequired(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return !isNaN(value);
  return Boolean(value);
}

/**
 * Validate required field with custom message
 * @param {string} value - Value to validate
 * @param {string} fieldName - Human-readable field name
 * @returns {Object} { isValid, message }
 */
export function validateRequiredWithMessage(value, fieldName = 'This field') {
  const isValid = validateRequired(value);
  return createValidationResult(isValid, ERROR_MESSAGES.required(fieldName));
}

/**
 * Validate phone number format (flexible international support)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether phone is valid
 */
export function validatePhone(phone) {
  if (!phone || typeof phone !== 'string') return true; // Phone is optional
  const cleaned = phone.replace(/[-\s().+]/g, '');
  if (cleaned.length === 0) return true; // Empty is OK
  // Allow 6-15 digits, optionally starting with + (already stripped)
  const re = /^[0-9]{6,15}$/;
  return re.test(cleaned);
}

/**
 * Validate phone with error message
 * @param {string} phone - Phone to validate
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Object} { isValid, message }
 */
export function validatePhoneWithMessage(phone, isRequired = false) {
  if (!phone || !phone.trim()) {
    return isRequired
      ? createValidationResult(false, ERROR_MESSAGES.required('Phone number'))
      : createValidationResult(true);
  }
  return createValidationResult(validatePhone(phone), ERROR_MESSAGES.phone);
}

// ============================================================================
// NUMERIC VALIDATORS WITH MESSAGES
// ============================================================================

/**
 * Validate a positive number
 * @param {number|string} value - Value to validate
 * @param {Object} options - Validation options
 * @param {number} options.min - Minimum allowed value
 * @param {number} options.max - Maximum allowed value
 * @param {boolean} options.allowZero - Whether zero is allowed
 * @returns {boolean} Whether value is valid
 */
export function validatePositiveNumber(value, options = {}) {
  const { min = 0, max = Infinity, allowZero = true } = options;
  
  const num = toNumber(value);
  
  if (isNaN(num)) return false;
  if (!allowZero && num === 0) return false;
  if (num < min) return false;
  if (num > max) return false;
  
  return num >= 0;
}

/**
 * Validate quantity (positive integer or decimal)
 * @param {number|string} value - Quantity to validate
 * @returns {boolean} Whether quantity is valid
 */
export function validateQuantity(value) {
  return validatePositiveNumber(value, { min: 0, allowZero: false });
}

/**
 * Validate quantity with message
 * @param {number|string} value - Quantity to validate
 * @returns {Object} { isValid, message }
 */
export function validateQuantityWithMessage(value) {
  const num = toNumber(value);
  if (isNaN(num) || num <= 0) {
    return createValidationResult(false, ERROR_MESSAGES.quantity);
  }
  return createValidationResult(true);
}

/**
 * Validate rate/price (non-negative number)
 * @param {number|string} value - Rate to validate
 * @returns {boolean} Whether rate is valid
 */
export function validateRate(value) {
  return validatePositiveNumber(value, { min: 0, allowZero: true });
}

/**
 * Validate rate with message
 * @param {number|string} value - Rate to validate
 * @returns {Object} { isValid, message }
 */
export function validateRateWithMessage(value) {
  const num = toNumber(value);
  if (isNaN(num) || num < 0) {
    return createValidationResult(false, ERROR_MESSAGES.rate);
  }
  return createValidationResult(true);
}

/**
 * Validate discount amount
 * @param {number|string} discount - Discount to validate
 * @param {number} subtotal - Subtotal to compare against
 * @returns {boolean} Whether discount is valid
 */
export function validateDiscount(discount, subtotal = Infinity) {
  const discountNum = toNumber(discount);
  if (discountNum < 0) return false;
  if (discountNum > subtotal) return false;
  return true;
}

/**
 * Validate discount with message
 * @param {number|string} discount - Discount to validate
 * @param {number} subtotal - Subtotal to compare against
 * @returns {Object} { isValid, message }
 */
export function validateDiscountWithMessage(discount, subtotal = Infinity) {
  const discountNum = toNumber(discount);
  if (isNaN(discountNum) || discountNum < 0) {
    return createValidationResult(false, ERROR_MESSAGES.discountNegative);
  }
  if (discountNum > subtotal && subtotal !== Infinity) {
    return createValidationResult(false, ERROR_MESSAGES.discount);
  }
  return createValidationResult(true);
}

/**
 * Validate tax rate (percentage between 0-100)
 * @param {number|string} rate - Tax rate to validate
 * @returns {boolean} Whether tax rate is valid
 */
export function validateTaxRate(rate) {
  return validatePositiveNumber(rate, { min: 0, max: 100 });
}

/**
 * Validate tax rate with message
 * @param {number|string} rate - Tax rate to validate
 * @returns {Object} { isValid, message }
 */
export function validateTaxRateWithMessage(rate) {
  const num = toNumber(rate);
  if (isNaN(num) || num < 0 || num > 100) {
    return createValidationResult(false, ERROR_MESSAGES.taxRate);
  }
  return createValidationResult(true);
}

// ============================================================================
// INVOICE VALIDATION
// ============================================================================

/**
 * Validation result object
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether the data is valid
 * @property {Object.<string, string>} errors - Field-specific error messages
 * @property {string[]} warnings - Non-blocking warnings
 */

/**
 * Validate a single line item
 * @param {Object} item - Line item to validate
 * @param {number} index - Index for error messages
 * @returns {ValidationResult} Validation result
 */
export function validateLineItem(item, index = 0) {
  const errors = {};
  const warnings = [];
  
  if (!item) {
    errors[`items[${index}]`] = 'Line item is required';
    return { isValid: false, errors, warnings };
  }
  
  // Description validation
  if (!validateRequired(item.description)) {
    errors[`items[${index}].description`] = 'Description is required';
  }
  
  // Quantity validation
  if (!validateQuantity(item.quantity)) {
    errors[`items[${index}].quantity`] = 'Quantity must be greater than 0';
  }
  
  // Rate validation
  if (!validateRate(item.rate)) {
    errors[`items[${index}].rate`] = 'Rate must be 0 or greater';
  }
  
  // Warnings for potentially unintended values
  if (toNumber(item.rate) === 0 && validateRequired(item.description)) {
    warnings.push(`Item "${item.description}" has a rate of 0`);
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate complete invoice form data
 * @param {Object} formData - Complete invoice form data
 * @returns {ValidationResult} Validation result with all errors
 */
export function validateInvoiceForm(formData) {
  const errors = {};
  const warnings = [];
  
  if (!formData) {
    return {
      isValid: false,
      errors: { form: 'Invoice data is required' },
      warnings: [],
    };
  }
  
  // ========== Business Information ==========
  if (!validateRequired(formData.business_name)) {
    errors.business_name = 'Business name is required';
  }
  
  if (formData.business_email && !validateEmail(formData.business_email)) {
    errors.business_email = 'Invalid email format';
  }
  
  if (formData.business_phone && !validatePhone(formData.business_phone)) {
    errors.business_phone = 'Invalid phone number format';
  }
  
  // ========== Client Information ==========
  if (!validateRequired(formData.client_name)) {
    errors.client_name = 'Client name is required';
  }
  
  if (formData.client_email && !validateEmail(formData.client_email)) {
    errors.client_email = 'Invalid email format';
  }
  
  if (formData.client_phone && !validatePhone(formData.client_phone)) {
    errors.client_phone = 'Invalid phone number format';
  }
  
  // ========== Invoice Details ==========
  // FEATURE #7: Invoice number is generated by backend on finalization
  // Skip invoice_number validation
  
  if (!validateRequired(formData.invoice_date)) {
    errors.invoice_date = 'Invoice date is required';
  }
  
  if (!validateRequired(formData.due_date)) {
    errors.due_date = 'Due date is required';
  }
  
  // Check due date is not before invoice date
  if (formData.invoice_date && formData.due_date) {
    if (new Date(formData.due_date) < new Date(formData.invoice_date)) {
      errors.due_date = 'Due date cannot be before invoice date';
    }
  }
  
  // ========== Line Items ==========
  if (!formData.items || formData.items.length === 0) {
    errors.items = 'At least one line item is required';
  } else if (!hasValidLineItems(formData.items)) {
    errors.items = 'At least one valid line item with description is required';
  } else {
    // Validate each line item
    formData.items.forEach((item, index) => {
      const itemValidation = validateLineItem(item, index);
      Object.assign(errors, itemValidation.errors);
      warnings.push(...itemValidation.warnings);
    });
  }
  
  // ========== Financial Values ==========
  if (!validateTaxRate(formData.tax_rate)) {
    errors.tax_rate = 'Tax rate must be between 0 and 100';
  }
  
  const subtotal = toNumber(formData.subtotal);
  if (!validateDiscount(formData.discount, subtotal)) {
    errors.discount = `Discount cannot exceed subtotal (${subtotal})`;
  }
  
  // ========== Warnings ==========
  if (!formData.business_email) {
    warnings.push('Business email is recommended for professional invoices');
  }
  
  if (!formData.client_email) {
    warnings.push('Client email is recommended for sending invoices');
  }
  
  if (toNumber(formData.total) === 0) {
    warnings.push('Invoice total is $0.00');
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
}

/**
 * Quick validation check for PDF generation
 * Only checks critical fields required for PDF
 * @param {Object} formData - Invoice form data
 * @returns {Object} { canGenerate: boolean, missingFields: string[] }
 */
export function validateForPdfGeneration(formData) {
  const missingFields = [];
  
  if (!validateRequired(formData?.business_name)) {
    missingFields.push('Business name');
  }
  
  if (!validateRequired(formData?.client_name)) {
    missingFields.push('Client name');
  }
  
  if (!hasValidLineItems(formData?.items)) {
    missingFields.push('At least one line item');
  }
  
  return {
    canGenerate: missingFields.length === 0,
    missingFields,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

const validatorsExports = {
  ERROR_MESSAGES,
  createValidationResult,
  validateEmail,
  validateEmailWithMessage,
  validateRequired,
  validateRequiredWithMessage,
  validatePhone,
  validatePhoneWithMessage,
  validatePositiveNumber,
  validateQuantity,
  validateQuantityWithMessage,
  validateRate,
  validateRateWithMessage,
  validateDiscount,
  validateDiscountWithMessage,
  validateTaxRate,
  validateTaxRateWithMessage,
  validateLineItem,
  validateInvoiceForm,
  validateForPdfGeneration,
};

export default validatorsExports;